import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getUserPayments } from '../../services/payment';

const PaymentStatus = () => {
  const { profile } = useAuth();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchPayments = async () => {
      if (profile) {
        try {
          const paymentsData = await getUserPayments(profile.uid);
          setPayments(paymentsData);
        } catch (error) {
          console.error('Error fetching payments:', error);
          setError('Failed to load payment history');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchPayments();
  }, [profile]);

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'yellow', text: 'Pending Review', icon: '⏳' },
      approved: { color: 'green', text: 'Approved', icon: '✅' },
      rejected: { color: 'red', text: 'Rejected', icon: '❌' }
    };
    
    return badges[status] || badges.pending;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getLevelName = (levelId) => {
    const levels = {
      grade7: 'Grade 7',
      form2: 'Form 2',
      grade12: 'Grade 12'
    };
    return levels[levelId] || levelId;
  };

  if (loading) {
    return (
      <div className="payment-status">
        <div className="loading-container">
          <div className="loading-spinner">Loading payment status...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-status">
      <div className="container">
        <div className="page-header">
          <h1>Payment Status</h1>
          <p>Track your subscription payment status</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        {payments.length === 0 ? (
          <div className="empty-state">
            <h3>No Payments Found</h3>
            <p>You haven't submitted any payments yet.</p>
            <div className="empty-actions">
              <Link to="/payment/instructions" className="btn btn-primary">
                Make Payment
              </Link>
              <Link to="/payment/submit" className="btn btn-secondary">
                Submit Payment Proof
              </Link>
            </div>
          </div>
        ) : (
          <div className="payments-timeline">
            <h2>Payment History</h2>
            
            {payments.map((payment, index) => {
              const statusBadge = getStatusBadge(payment.status);
              
              return (
                <div key={payment.id} className="payment-timeline-item">
                  <div className="timeline-marker">
                    <div className={`marker ${statusBadge.color}`}>
                      {statusBadge.icon}
                    </div>
                    {index < payments.length - 1 && <div className="timeline-line"></div>}
                  </div>
                  
                  <div className="timeline-content">
                    <div className="payment-card">
                      <div className="payment-header">
                        <div className="payment-info">
                          <h3>{getLevelName(payment.levelId)} Subscription</h3>
                          <div className={`status-badge ${statusBadge.color}`}>
                            {statusBadge.text}
                          </div>
                        </div>
                        <div className="payment-amount">
                          K{payment.amount}
                        </div>
                      </div>
                      
                      <div className="payment-details">
                        <div className="detail-grid">
                          <div className="detail-item">
                            <label>Transaction ID:</label>
                            <span>{payment.transactionId}</span>
                          </div>
                          <div className="detail-item">
                            <label>Phone Number:</label>
                            <span>{payment.phoneNumber}</span>
                          </div>
                          <div className="detail-item">
                            <label>Payment Date:</label>
                            <span>{formatDate(payment.paymentDate)}</span>
                          </div>
                          <div className="detail-item">
                            <label>Submitted:</label>
                            <span>{formatDate(payment.createdAt)}</span>
                          </div>
                        </div>
                        
                        {payment.status === 'approved' && (
                          <div className="approval-details">
                            <label>Approved:</label>
                            <span>{formatDate(payment.approvedAt)}</span>
                          </div>
                        )}
                        
                        {payment.status === 'rejected' && (
                          <div className="rejection-details">
                            <label>Rejection Reason:</label>
                            <p>{payment.rejectionReason || 'No reason provided'}</p>
                          </div>
                        )}
                        
                        {payment.notes && (
                          <div className="payment-notes">
                            <label>Notes:</label>
                            <p>{payment.notes}</p>
                          </div>
                        )}
                        
                        {payment.proofOfPaymentUrl && (
                          <div className="payment-proof">
                            <label>Proof of Payment:</label>
                            <a 
                              href={payment.proofOfPaymentUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="proof-link"
                            >
                              View Proof
                            </a>
                          </div>
                        )}
                      </div>
                      
                      <div className="payment-actions">
                        {payment.status === 'pending' && (
                          <p className="pending-note">
                            Your payment is being reviewed. This usually takes 24-48 hours.
                          </p>
                        )}
                        
                        {payment.status === 'approved' && (
                          <div className="approved-actions">
                            <p className="approved-note">
                              🎉 Your subscription is now active! You can access all study materials.
                            </p>
                            <Link to="/dashboard" className="btn btn-primary">
                              Go to Dashboard
                            </Link>
                          </div>
                        )}
                        
                        {payment.status === 'rejected' && (
                          <div className="rejected-actions">
                            <p className="rejected-note">
                              Your payment was rejected. Please review the reason and submit again.
                            </p>
                            <Link to="/payment/submit" className="btn btn-primary">
                              Submit New Payment
                            </Link>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        <div className="help-section">
          <h3>Need Help?</h3>
          <div className="help-cards">
            <div className="help-card">
              <h4>Payment Pending?</h4>
              <p>Payments are typically reviewed within 24-48 hours during business days.</p>
            </div>
            <div className="help-card">
              <h4>Payment Rejected?</h4>
              <p>Check the rejection reason and submit a new payment with correct details.</p>
            </div>
            <div className="help-card">
              <h4>Contact Support</h4>
              <p>If you have any issues, reach out to our support team for assistance.</p>
            </div>
          </div>
          
          <div className="help-actions">
            <Link to="/contact" className="btn btn-secondary">
              Contact Support
            </Link>
            <Link to="/payment/instructions" className="btn btn-outline">
              Payment Instructions
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentStatus;
